/**
 * Silencia errores de analytics bloqueados por adblockers
 * Dominios comunes bloqueados: posthog.com, cloudflareinsights.com, metricool.com, facebook.net
 * 
 * IMPORTANTE: NO interfiere con el user flow - solo silencia errores de consola de analytics
 * NO intercepta eventos de error que puedan afectar la funcionalidad del sitio
 */
export const initAnalyticsErrorHandler = () => {
  if (typeof window === 'undefined') return;

  const originalError = console.error;
  const originalWarn = console.warn;

  // Lista de patterns a ignorar (analytics bloqueados)
  const blockedPatterns = [
    'ERR_BLOCKED_BY_CLIENT',
    'posthog.com',
    'posthog.i.posthog',
    'us.i.posthog.com',
    'us-assets.i.posthog.com',
    'cloudflareinsights.com',
    'metricool.com',
    'facebook.net',
    'fbevents.js',
    'connect.facebook.net',
    '/beacon.min.js',
    'tracker.metricool.com',
    'analytics.ahrefs.com',
    'ahrefs.com',
    'datafa.st',
    'DataFast',
    'datafast',
    'inferred.litix.io',
    'litix.io',
    'axiom.co',
    'us-east-1.aws.edge.axiom.co',
    'vercel/ingest',
    'web-vitals',
    // Browser CORS noise from 3rd-party analytics integrations
    'Access-Control-Allow-Origin',
    'CORS policy',
    'preflight request',
    'ERR_FAILED',
    // Preload warnings (mux HLS preload is intentionally removed; suppress stale warnings)
    'preloaded using link preload but not used',
    'was preloaded',
  ];

  // Función para verificar si un error es de analytics bloqueado
  const isBlockedAnalyticsError = (errorString: string): boolean => {
    return blockedPatterns.some(pattern => errorString.includes(pattern));
  };

  // SOLO interceptar console.error y console.warn - NO tocar eventos de error
  // Esto asegura que NO interfiere con el user flow
  console.error = (...args: unknown[]) => {
    const errorString = args.join(' ');
    
    // NO silenciar errores de video/Mux/red - son críticos para el user flow
    if (errorString.includes('mux.com') || 
        errorString.includes('stream.mux.com') ||
        errorString.includes('image.mux.com') ||
        errorString.includes('video') ||
        errorString.includes('media') ||
        errorString.includes('manifest') ||
        errorString.includes('network') ||
        errorString.includes('connection') ||
        errorString.includes('tunnel') ||
        errorString.includes('ERR_TUNNEL') ||
        errorString.includes('Network Error')) {
      originalError.apply(console, args);
      return;
    }
    
    // Solo silenciar errores de analytics bloqueados
    if (isBlockedAnalyticsError(errorString)) {
      return; // Silenciar solo errores de analytics
    }
    originalError.apply(console, args);
  };

  // SOLO interceptar console.warn - NO tocar eventos de error
  console.warn = (...args: unknown[]) => {
    const warnString = args.join(' ');
    
    // NO silenciar warnings de video/Mux/red - son críticos para el user flow
    if (warnString.includes('mux.com') || 
        warnString.includes('stream.mux.com') ||
        warnString.includes('image.mux.com') ||
        warnString.includes('video') ||
        warnString.includes('media') ||
        warnString.includes('network') ||
        warnString.includes('connection')) {
      originalWarn.apply(console, args);
      return;
    }
    
    // Solo silenciar warnings de analytics bloqueados
    if (isBlockedAnalyticsError(warnString)) {
      return; // Silenciar solo warnings de analytics
    }
    originalWarn.apply(console, args);
  };

  // NO interceptar window.onerror - puede afectar el user flow
  // Los errores de JavaScript deben pasar normalmente para que los componentes los manejen

  // NO interceptar unhandledrejection - puede afectar el user flow
  // Las promesas rechazadas deben pasar normalmente

  // NO interceptar eventos de error de recursos - puede afectar el user flow
  // Los errores de recursos deben pasar normalmente para que los componentes los manejen
};
